import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useCases = [
  { icon: '🔄', title: 'Process Automation', desc: 'Eliminate manual workflows in AP/AR, scheduling, reporting, and more.' },
  { icon: '🤝', title: 'Customer Operations', desc: 'Lead scoring, follow-up automation, CRM enrichment, and churn detection.' },
  { icon: '📊', title: 'Business Intelligence', desc: 'Automated dashboards and AI-generated insights delivered to leadership.' },
  { icon: '👥', title: 'HR & Recruiting', desc: 'Resume screening, interview scheduling, onboarding knowledge bases.' },
  { icon: '📄', title: 'Document Processing', desc: 'Contract extraction, summarization, renewal tracking, and compliance.' },
  { icon: '🛠️', title: 'Field Service & Ops', desc: 'Route optimization, dispatch automation, technician scheduling.' },
];

const industries = ['Professional Services', 'Nonprofits & Cultural Orgs', 'Healthcare Practices', 'Retail & Distribution', 'Field Service & Trades'];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Sagient Partners | Agentic AI Consulting for Nonprofit, Small and Mid-Sized Businesses</title>
        <meta name="description" content="Practical AI Implementation for Nonprofits, Small and Mid-Market Companies – Led by Founders with 35+ Years of Operational Experience" />
        <link rel="canonical" href="https://sagientpartners.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sagientpartners.com/" />
        <meta property="og:title" content="Sagient Partners | Agentic AI Consulting for Nonprofits and Small Businesses" />
        <meta property="og:description" content="Sagient Partners helps nonprofits, small and mid-sized businesses adopt AI - from readiness assessments to full implementation. Based in the San Francisco Bay Area." />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Sagient Partners | Agentic AI Consulting for Nonprofits and Small Businesses" />
        <meta name="twitter:description" content="Sagient Partners helps nonprofits, small and mid-sized businesses adopt AI - from readiness assessments to full implementation. Based in the San Francisco Bay Area." />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Sagient Partners",
          "url": "https://sagientpartners.com",
          "logo": "https://sagientpartners.com/sagient_partners_logo.png",
          "description": "Agentic AI consulting for small and mid-market businesses. We help SMBs adopt and operationalize AI through hands-on strategy and implementation.",
          "areaServed": "US",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "info@sagientpartners.com",
            "contactType": "customer service",
            "url": "https://sagientpartners.com/contact"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "AI Consulting Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Readiness Assessment",
                  "description": "A one-time engagement to evaluate your business operations and identify where AI can deliver the highest return.",
                  "offers": { "@type": "Offer", "priceCurrency": "USD", "priceRange": "$3,000–$5,000" }
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Strategy Advisory",
                  "description": "Project-based AI strategy and implementation guidance tailored to your business goals.",
                  "offers": { "@type": "Offer", "priceCurrency": "USD", "priceRange": "$5,000–$10,000" }
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Fractional AI Advisor",
                  "description": "Ongoing monthly retainer providing continuous AI strategy, oversight, and optimization for your business.",
                  "offers": { "@type": "Offer", "priceCurrency": "USD", "priceRange": "$2,000–$4,000/month" }
                }
              }
            ]
          }
        })}</script>
      </Helmet>
      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)',
        color: '#fff', padding: '100px 0 80px', position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative orb */}
        <div style={{
          position: 'absolute', top: -80, right: -80, width: 480, height: 480,
          borderRadius: '50%', background: 'rgba(55,138,221,0.15)', pointerEvents: 'none',
        }} />
        <div className="container" style={{ position: 'relative' }}>
          <p className="section-label" style={{ color: '#B5D4F4' }}>Agentic AI Consulting</p>
          <h1 style={{ color: '#fff', maxWidth: 700, marginBottom: 16, fontSize: 'clamp(1.6rem, 4vw, 2.4rem)' }}>
            AI Consulting for Nonprofits, Small and Mid-Sized Businesses
          </h1>
          <p style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 500, maxWidth: 620, marginBottom: 20, lineHeight: 1.4 }}>
            AI That Works the Way Your Business Does
          </p>
          <p style={{ color: '#B5D4F4', fontSize: '1.15rem', maxWidth: 580, marginBottom: 40, lineHeight: 1.75 }}>
            Sagient Partners helps nonprofit organizations, small and mid-market companies adopt and operationalize AI -
            strategically and practically, with founders who've done it themselves.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-white">Book a Discovery Call</Link>
            <Link to="/services" className="btn btn-outline" style={{ borderColor: '#B5D4F4', color: '#B5D4F4' }}>
              Explore Services →
            </Link>
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 48, marginTop: 64, flexWrap: 'wrap' }}>
            {[['35+', 'Years combined experience'], ['3', 'Engagement tiers']].map(([val, label]) => (
              <div key={val}>
                <p style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>{val}</p>
                <p style={{ color: '#8BAFC8', fontSize: '0.9rem' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Value prop ── */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            {[
              {
                icon: '🧭',
                title: 'Strategic, Not Just Technical',
                desc: 'We speak the language of business owners, not just technologists. Strategy first, tools second.',
              },
              {
                icon: '🏗️',
                title: 'Operators Who\'ve Built It',
                desc: 'Two-time founders with SaaS, P&L, and hands-on AI implementation experience - not theoretical advice.',
              },
              {
                icon: '📈',
                title: 'Measurable ROI',
                desc: 'Every engagement produces a prioritized roadmap with ROI analysis tied to your actual operations.',
              },
            ].map(item => (
              <div key={item.title} className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.2rem', marginBottom: 16 }}>{item.icon}</div>
                <h3 style={{ marginBottom: 12 }}>{item.title}</h3>
                <p style={{ fontSize: '0.95rem' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services snapshot ── */}
      <section className="section section--alt">
        <div className="container">
          <p className="section-label">How We Engage</p>
          <h2 className="section-heading">Three Ways to Work Together</h2>
          <p className="section-sub">From a focused audit to ongoing strategic partnership - meet your business where it is.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 28 }}>
            {[
              { tier: 'Tier 1', name: 'AI Readiness Assessment', time: '2–4 weeks', desc: 'Audit your operations, identify AI opportunities, and leave with a prioritized implementation roadmap.' },
              { tier: 'Tier 2', name: 'AI Strategy Implementation', time: '4–8 weeks', desc: 'Tool selection, implementation, and the people side - role changes and training that make adoption real.' },
              { tier: 'Tier 3', name: 'AI Advisor', time: 'Ongoing retainer', desc: 'Scheduled reviews, new automations, performance monitoring, and executive reporting.' },
            ].map(s => (
              <div key={s.tier} className="card" style={{ borderTop: '4px solid #378ADD' }}>
                <span className="tag" style={{ marginBottom: 16 }}>{s.tier}</span>
                <h3 style={{ marginBottom: 8 }}>{s.name}</h3>
                <p style={{ fontSize: '0.85rem', color: '#378ADD', marginBottom: 16 }}>{s.time}</p>
                <p style={{ fontSize: '0.93rem' }}>{s.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/services" className="btn btn-primary">See Full Service Details →</Link>
          </div>
        </div>
      </section>

      {/* ── Use cases ── */}
      <section className="section">
        <div className="container">
          <p className="section-label">What We Automate</p>
          <h2 className="section-heading">Common AI Use Cases</h2>
          <p className="section-sub">Real workflows that most businesses can automate in weeks - not months.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {useCases.map(u => (
              <div key={u.title} style={{ padding: '24px', borderRadius: 10, border: '1px solid #EBF4FD', background: '#fff' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 12 }}>{u.icon}</div>
                <h3 style={{ marginBottom: 8, fontSize: '1rem' }}>{u.title}</h3>
                <p style={{ fontSize: '0.88rem' }}>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="section section--alt">
        <div className="container">
          <p className="section-label">Who We Serve</p>
          <h2 className="section-heading">Built for Non-Tech Businesses</h2>
          <p className="section-sub">We focus on industries running on people and manual processes - where AI has the biggest impact.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {industries.map(i => (
              <span key={i} className="tag" style={{ fontSize: '0.92rem', padding: '8px 18px' }}>{i}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background: 'linear-gradient(135deg, #378ADD, #0C447C)', padding: '80px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16 }}>Ready to See What AI Can Do for Your Business?</h2>
          <p style={{ color: '#B5D4F4', fontSize: '1.05rem', marginBottom: 36 }}>
            Start with a free 30-minute discovery call. No jargon, no pressure - just a clear picture of what's possible.
          </p>
          <Link to="/contact" className="btn btn-white" style={{ fontSize: '1rem' }}>Book Your Discovery Call →</Link>
        </div>
      </section>
    </>
  );
}
